import isEmpty from 'es-toolkit/compat/isEmpty';
import trim from 'es-toolkit/compat/trim';
import { eval as astEval, parse } from 'expression-eval';
import {
  RemoveUnresolvedTokens,
  TokenReplace,
  type TokenValue,
} from './TokenReplace';

/**
 * Check if value is a number
 * @param value a string or number
 * @returns true if it can be parsed as a valid number, false otherwise
 * @link https://stackoverflow.com/a/175787
 */
function isNumeric(value: string | number): boolean {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  return /^-?\d+$/.test(value);
}

/**
 * Check if operator args are numeric, and if not, log which ones
 * @param operatorName the operator to log if arguments are not numeric
 * @param operatorArgs the arguments to check numeric-ness
 * @returns true if all arguments are numeric, false if not
 */
function NumericCheck(operatorName: string, ...operatorArgs: string[]) {
  const argsAreNumbers = operatorArgs.map(isNumeric);
  if (argsAreNumbers.every((v) => v === true)) return true;
  console.groupCollapsed('Visibility Error');
  console.error(
    `${`Could not evaluate visibility rule: '${
      operatorArgs[0]
    } ${operatorName} ${operatorArgs[1] ?? ''}`.trim()}'`
  );
  argsAreNumbers.forEach((v, i) => {
    if (!v) console.info(`'${operatorArgs[i]}' is not numeric`);
  });
  console.groupEnd();
  return false;
}
/**
 * Replaces all instances of tokens in the string with their replacement
 * @param str the string to replace tokens in
 * @param tokens the tokens to use for replacement
 * @returns a string with token values replaced
 */
function EvaluateToken(str: string, tokens: TokenValue[] = []): string {
  let newStr = str.slice();
  newStr = TokenReplace(newStr, tokens);
  newStr = RemoveUnresolvedTokens(newStr);
  return newStr;
}

/**
 * Unary camilyo visibility operators
 */
const operators = {
  'not-empty': (value: string) => !isEmpty(trim(value, "'")),
  'is-empty': (value: string) => isEmpty(trim(value, "'")),
  'is-even': (value: string) => {
    if (!NumericCheck('is-even', value)) return false;
    const number = Number.parseFloat(value);
    return number % 2 === 0;
  },
  'is-odd': (value: string) => {
    if (!NumericCheck('is-odd', value)) return false;
    const number = Number.parseFloat(value);
    return number % 2 === 1;
  },
  'is-positive': (value: string) => {
    if (!NumericCheck('is-positive', value)) return false;
    const number = Number.parseFloat(value);
    return number >= 0;
  },
  'is-negative': (value: string) => {
    if (!NumericCheck('is-negative', value)) return false;
    const number = Number.parseFloat(value);
    return number < 0;
  },
};

/**
 * Binary camilyo visibility operators
 */
const binary_operators = {
  '==': (value1: string, value2: string) => {
    if (isNumeric(value1) && isNumeric(value2)) {
      return Number.parseFloat(value1) === Number.parseFloat(value2);
    }
    return value1 === value2;
  },
  '!=': (value1: string, value2: string) => {
    return !binary_operators['=='](value1, value2);
  },
  '!~': (value1: string, value2: string) => {
    return !binary_operators['~'](value1, value2);
  },
  '~': (value1: string, value2: string) => {
    return value1.includes(value2);
  },
  regex: (value1: string, value2: string) => {
    try {
      const regex = new RegExp(value2);
      return regex.test(value1);
    } catch (e) {
      console.group('Regex Error');
      console.info(`Visibility regex error: '${value1} regex ${value2}'`);
      console.error(e);
      console.groupEnd();
      return false;
    }
  },
  todayIsAfter: (_value1: string, _value2: string) => {
    throw new Error('todayIsAfter unimplemented');
  },
  todayIsBefore: (_value1: string, _value2: string) => {
    throw new Error('todayIsBefore unimplemented');
  },
  todayIsBetween: (_value1: string, _value2: string) => {
    throw new Error('todayIsBetween unimplemented');
  },
  'greater-than': (value1: string, value2: string) => {
    if (!NumericCheck('greater-than', value1, value2)) return false;
    const number1 = Number.parseFloat(value1);
    const number2 = Number.parseFloat(value2);
    return number1 > number2;
  },
  'lower-than': (value1: string, value2: string) => {
    if (!NumericCheck('lower-than', value1, value2)) return false;
    const number1 = Number.parseFloat(value1);
    const number2 = Number.parseFloat(value2);
    return number1 < number2;
  },
  'greater-equal-than': (value1: string, value2: string) => {
    if (!NumericCheck('greater-equal-than', value1, value2)) return false;
    const number1 = Number.parseFloat(value1);
    const number2 = Number.parseFloat(value2);
    return number1 >= number2;
  },
  'lower-equal-than': (value1: string, value2: string) => {
    if (!NumericCheck('lower-equal-than', value1, value2)) return false;
    const number1 = Number.parseFloat(value1);
    const number2 = Number.parseFloat(value2);
    return number1 <= number2;
  },
  contains: (value1: string, value2: string) => {
    value2 = value2.trim();
    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(value1);
    } catch {
      parsedValue = value1.trim();
    }
    if (Array.isArray(parsedValue)) {
      return parsedValue.includes(value2);
    }
    if (typeof parsedValue === 'object') {
      // Return true if key exists and its value is truthy
      return (
        Object.keys(parsedValue as object).includes(value2) &&
        Boolean((parsedValue as Record<string, any>)[value2])
      );
    }
    return binary_operators['=='](value1, value2);
  },
};

type Operator = keyof typeof operators;
type BinaryOperator = keyof typeof binary_operators;

export function visible(value: string, tokens: TokenValue[] = []): boolean {
  let evaluated = false;
  try {
    evaluated = value
      .split('&&')
      .map((andElement) => {
        return andElement
          .split('||')
          .map((orElement) => visibleHelper(orElement, tokens))
          .includes(true);
      })
      .every((element) => element === true);
  } catch (e) {
    console.groupCollapsed('Visibility Error');
    console.info(`Parsing: '${value}'`);
    console.error(e);
    console.groupEnd();
  }
  return evaluated;
}

function visibleHelper(value: string, tokens: TokenValue[] = []): boolean {
  let evaluated = null;
  // If value starts with a "!" character, negate the evaluation
  const negate = value.startsWith('!');
  if (negate) value = value.substring(1);
  const operator_name: Operator | undefined = Object.keys(operators).find(
    (element) => value.indexOf(element) !== -1
  ) as Operator;
  const bioperator_name: BinaryOperator | undefined = Object.keys(
    binary_operators
  ).find((element) => value.indexOf(element) !== -1) as BinaryOperator;
  if (operator_name) {
    const expression = value.split(operator_name)[0].trim();
    evaluated = operators[operator_name](EvaluateToken(expression, tokens));
  } else if (bioperator_name) {
    const [expression1, expression2] = value
      .split(bioperator_name)
      .map((v) => v.trim());

    evaluated = binary_operators[bioperator_name](
      EvaluateToken(expression1, tokens),
      EvaluateToken(expression2, tokens)
    );
  }

  if (evaluated == null) {
    const parsedAST = parse(EvaluateToken(value, tokens));
    // Evaluate to bool with JS truthiness
    evaluated = Boolean(astEval(parsedAST, {}));
  }
  return negate ? !evaluated : evaluated;
}
